// One entry in a list — the feed's row, and the same row inside a journal.
//
// It is a container of two siblings rather than a single button: `.eopen` covers the picture and the
// words so the row reads as one target, and whatever the list needs on the right sits beside it.
// A button cannot legally contain another one, and a row that both opens and deletes needs two.

import { useRef, type ReactNode } from 'react';
import { IconTrash } from '../components/Icons';
import { entryPreview } from './preview';
import { EntryArt, useNearbyPhoto } from './EntryArt';
import { snippet, timeLabel } from '../screens/home-support';
import { longDate } from '../format/dates';
import type { DreamType, Entry } from '../api/entries';

const DREAM_TYPE_LABEL: Record<DreamType, string> = {
  normal: 'Dream',
  lucid: 'Lucid',
  nightmare: 'Nightmare',
};

/** iOS writes "nightmare"; the stylesheet has always called that state `night`. */
export function dreamTypeClass(type: string): string {
  if (type === 'lucid') return 'lucid';
  if (type === 'nightmare') return 'night';
  return 'normal';
}

/**
 * Entries written before the dream types were narrowed to the three iOS understands can still carry
 * "recurring", "prophetic" or "healing" in the cleartext column, so the label has to tolerate a value
 * the type no longer admits rather than rendering "undefined" over someone's own dream.
 */
export function dreamTypeLabel(type: string): string {
  return DREAM_TYPE_LABEL[type as DreamType] ?? type.charAt(0).toUpperCase() + type.slice(1);
}

/** What an entry is called when it never got a title: a draft says its day, the rest say so. */
export function entryLabel(entry: Entry): string {
  return entry.title || (entry.isDraft ? longDate(entry.createdAt) : 'Untitled');
}

export function EntryRow({
  entry,
  onOpen,
  onDelete,
  trailing,
  when,
}: {
  entry: Entry;
  onOpen: () => void;
  /** Offers delete on the row. Ignored when `trailing` fills the same slot. */
  onDelete?: () => void;
  /** What a journal puts there instead: a remove button, or the "in N journals" count. */
  trailing?: ReactNode;
  /**
   * What the row says about when. The time of day by default, which is all a row needs in a list
   * already grouped under its date — a list that isn't, like a symbol's occurrences spread over
   * months, passes the date instead.
   */
  when?: string;
}) {
  const isDream = entry.kind === 'dream';

  // The same picture the gallery would give this entry, at thumbnail size. Sharing the hook means
  // the row inherits the gallery's restraint: nothing is fetched or decrypted until the row is
  // actually near the viewport, and the object URL is revoked when it leaves.
  const row = useRef<HTMLDivElement>(null);
  const photo = useNearbyPhoto(entry.id, row);
  const preview = entryPreview(entry, photo?.url, photo?.crop);

  return (
    <div ref={row} className="oentry">
      <button className="eopen" onClick={onOpen}>
        <EntryArt preview={preview} className="oentry-art" />

        <div className="oentry-body">
          <div className="etop">
            {/* An unfinished entry says so first, before it says what kind it is — the same trade
                Gmail makes, where "Draft" is the first thing on the row and in red. */}
            {entry.isDraft ? (
              <span className="edraft">Draft</span>
            ) : (
              <span className={`ekind ${isDream ? 'dream' : 'waking'}`}>
                <span className="dot" />
                {isDream ? 'Dreamt' : 'Visited'}
              </span>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isDream && entry.dreamType ? (
                <span className={`etype ${dreamTypeClass(entry.dreamType)}`}>
                  {dreamTypeLabel(entry.dreamType)}
                </span>
              ) : null}
              <span className="etime">{when ?? timeLabel(entry.createdAt)}</span>
            </div>
          </div>

          <h3 className="etitle">{entryLabel(entry)}</h3>
          <p className="esnip">{snippet(entry.body)}</p>

          {entry.symbols.length > 0 ? (
            <div className="esym">
              {entry.symbols.slice(0, 4).map((symbol) => (
                <span key={symbol}>{symbol}</span>
              ))}
            </div>
          ) : null}
        </div>
      </button>

      {trailing ? <div className="ctrail">{trailing}</div> : null}

      {!trailing && onDelete ? (
        <button
          className="edel"
          onClick={onDelete}
          aria-label={`Delete ${entry.title || (entry.isDraft ? 'draft' : 'entry')}`}
          title="Delete"
        >
          <IconTrash />
        </button>
      ) : null}
    </div>
  );
}
